import { formatDate } from '@angular/common'
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core'
import { GlobalMessageType, RoutingService } from '@spartacus/core'
import { LaunchDialogService } from '@spartacus/storefront'
import { Subscription } from 'rxjs'
import { finalize, take } from 'rxjs/operators'
import { AgreementStatus, AgreementStatusDisplay } from '../../../../../models/enum/agreement.enum'
import { LAUNCH_CALLER } from '../../../../../models/enum/custom-dialog.enum'
import { CustomRoutes } from '../../../../../models/enum/custom-routes.enum'
import { SharedValue } from '../../../../../models/enum/shared.enum'
import { ICsv } from '../../../../../models/interface/csv.interface'
import { IHistoryList } from '../../../../../models/interface/history-list.interface'
import { ISharedPagination } from '../../../../../models/interface/shared.interface'
import { DispenserAgreementService } from '../../../../../services/agreement/dispenser/dispenser-agreement.service'
import { CsvService } from '../../../../../services/shared/csv/csv.service'
import { CustomGlobalMessageService } from '../../../../../services/shared/custom-global-message/custom-global-message.service'
import { SharedService } from '../../../../../services/shared/shared.service'
import { ICustomSelectOption } from '../../../../shared/custom-components/custom-select/modals/interface/custom-select.interface'
import { CustomTableColumnType } from '../../../../shared/custom-components/custom-table/modals/enum/custom-table.enum'
import {
  ICustomTableColumn,
  ICustomTableHeader
} from '../../../../shared/custom-components/custom-table/modals/interface/custom-table.interface'

@Component({
  selector: 'app-dispenser-agreement-history',
  templateUrl: './dispenser-agreement-history.component.html',
  styleUrls: ['./dispenser-agreement-history.component.scss'],
  standalone: false
})
export class DispenserAgreementHistoryComponent implements OnInit, OnDestroy {
  public agreementList: IHistoryList
  public showInitialLoader = true
  public showLoaderOnPagination: boolean
  public selectedSort: string
  public selectedSearch: string
  public selectedPage = 1
  public downloadingOpenShipmentsOrdersCsv: boolean
  public selectedFilter: string
  @ViewChild('element') element: ElementRef
  private subscription = new Subscription()

  public filterOptions: ICustomSelectOption[] = [
    { label: 'dispenserAgreement.placed', value: `${AgreementStatus.OFFER},${AgreementStatus.SUBMITTED}` },
    { label: 'dispenserAgreement.accepted', value: AgreementStatus.ACCEPTED },
    { label: 'dispenserAgreement.rejected', value: AgreementStatus.REJECTED },
    { label: 'dispenserAgreement.pending', value: AgreementStatus.PENDING },
    { label: 'dispenserAgreement.expired', value: AgreementStatus.EXPIRED },
    { label: 'dispenserAgreement.reviewing', value: AgreementStatus.REVIEWING },
    { label: 'dispenserAgreement.waitingSignedAgreement', value: AgreementStatus.WAITING_SIGNED_AGREMMENT },
    { label: 'dispenserAgreement.cancelled', value: AgreementStatus.CANCELLED }
  ]

  public options: ICustomSelectOption[] = [
    { label: 'dispenserAgreement.dateCreated', value: 'startDate' },
    { label: 'dispenserAgreement.agreementNumber', value: 'code' }
  ]

  public headers: ICustomTableHeader[] = [
    { title: 'dispenserAgreement.agreementNumber' },
    { title: 'dispenserAgreement.dateCreated' },
    { title: 'dispenserAgreement.endCustomerName' },
    { title: 'dispenserAgreement.endCustomerBN' },
    { title: 'dispenserAgreement.approvedAssets' },
    { title: 'dispenserAgreement.remainingQuantity' },
    { title: 'shared.status' },
    { title: 'shared.actions', type: CustomTableColumnType.BUTTONS }
  ]

  /**
   * using displayInStatus to store in which status a actionbutton should be displayed.
   * using AgreementStatusDisplay Enum value since it's whats available in row data for later comparison.
   * if no displayInStatus is defined, the action button will show in every agreement status
   */
  public columns: ICustomTableColumn[] = [
    { key: 'orderCode', type: CustomTableColumnType.LINK, link: { handler: (res) => this.viewDetails(res) } },
    { key: 'startDate', type: CustomTableColumnType.DATE },
    { key: 'endCustomer.orgUnit.name' },
    { key: 'endCustomer.orgUnit.businessNumber' },
    { key: 'approvedAssets' },
    { key: 'remainingAssets' },
    { key: 'stateDisplay' },
    {
      key: 'actions',
      type: CustomTableColumnType.BUTTONS,
      buttons: [
        {
          title: 'shared.details',
          translate: true,
          handler: (res) => this.viewDetails(res),
          icon: 'fa fa-info-circle'
        },
        {
          title: 'dispenserAgreement.placeOrder',
          translate: true,
          handler: (res) => this.placeOrder(res),
          icon: 'fas fa-file-medical',
          displayInStatus: [AgreementStatusDisplay.ACCEPTED]
        },
        {
          title: 'dispenserAgreement.updateDispenserAgreement',
          translate: true,
          handler: (res) => this.openUpdateDispenserAgreementInfoModal(res?.orderCode),
          icon: 'fa-solid fa-pen',
          displayInStatus: [AgreementStatusDisplay.ACCEPTED]
        },
        {
          title: 'dispenserAgreement.cancelDispenserAgreement',
          translate: true,
          handler: (res) => this.openCancelDispenserAgreementModal(res?.orderCode),
          icon: 'fa-solid fa-ban',
          displayInStatus: [AgreementStatusDisplay.SUBMITTED]
        }
      ]
    }
  ]

  constructor(
    public dispenserAgreementService: DispenserAgreementService,
    public globalMessageService: CustomGlobalMessageService,
    public routingService: RoutingService,
    private changeDetectorRef: ChangeDetectorRef,
    private sharedService: SharedService,
    private csvService: CsvService,
    protected launchDialogService: LaunchDialogService,
    private vcr: ViewContainerRef
  ) {}

  ngOnInit(): void {
    this.getQueryParams()
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }

  private getQueryParams(): void {
    this.sharedService
      .getQueryParams()
      .subscribe((queryParams) => {
        if (queryParams?.page) {
          this.pageChange(queryParams.page)
        } else {
          this.pageChange(1)
        }
      })
      .unsubscribe()
  }

  public openShipmentCreditClaims(): void {
    // TODO:Spartacus - 'RoutingService.go' changed signature. Before 4.0, the object with query params could be passed in the 2nd argument. Now the 2nd argument is Angular NavigationExtras object (with 'queryParams' property).
    this.routingService.go({
      cxRoute: CustomRoutes.SHIPMENT_CREDIT_CLAIM
    })
  }

  public createNewAgreement(): void {
    // TODO:Spartacus - 'RoutingService.go' changed signature. Before 4.0, the object with query params could be passed in the 2nd argument. Now the 2nd argument is Angular NavigationExtras object (with 'queryParams' property).
    this.routingService.go({
      cxRoute: CustomRoutes.CREATE_DISPENSER_AGREEMENT
    })
  }

  public createDispenserCreditRequest(): void {
    // TODO:Spartacus - 'RoutingService.go' changed signature. Before 4.0, the object with query params could be passed in the 2nd argument. Now the 2nd argument is Angular NavigationExtras object (with 'queryParams' property).
    this.routingService.go({
      cxRoute: CustomRoutes.NEW_DISPENSER_CREDIT_REQUEST
    })
  }

  public viewDetails(value: any): void {
    const queryParam = {
      [CustomRoutes.DISPENSER_AGREEMENT_HISTORY]: { page: this.selectedPage }
    }
    this.sharedService.setQueryParamsLocal(queryParam)

    // TODO:Spartacus - 'RoutingService.go' changed signature. Before 4.0, the object with query params could be passed in the 2nd argument. Now the 2nd argument is Angular NavigationExtras object (with 'queryParams' property).
    this.routingService.go({
      cxRoute: CustomRoutes.DISPENSER_AGREEMENT_DETAILS,
      params: { code: value?.orderCode }
    })
  }

  public placeOrder(value: any): void {
    // TODO:Spartacus - 'RoutingService.go' changed signature. Before 4.0, the object with query params could be passed in the 2nd argument. Now the 2nd argument is Angular NavigationExtras object (with 'queryParams' property).
    this.routingService.go({
      cxRoute: CustomRoutes.DISPENSER_AGREEMENT_PLACE_ORDER,
      params: { code: value?.orderCode }
    })
  }

  public openUpdateDispenserAgreementInfoModal(dispenserAgreementCode: string) {
    const dialog = this.launchDialogService.openDialog(
      LAUNCH_CALLER.UPDATE_DISPENSER_AGREEMENT_INFO,
      this.element,
      this.vcr,
      dispenserAgreementCode
    )

    if (dialog) {
      this.subscription.add(dialog.pipe(take(1)).subscribe())
    }
  }

  private openCancelDispenserAgreementModal(dispenserAgreementCode: string) {
    const dialog = this.launchDialogService.openDialog(
      LAUNCH_CALLER.CANCEL_DISPENSER_AGREEMENT,
      this.element,
      this.vcr,
      dispenserAgreementCode
    )

    if (dialog) {
      this.subscription.add(dialog.pipe(take(1)).subscribe())
    }
  }

  // public pageChange(page: number): void {
  //      this.routingService.go({
  //           cxRoute: CustomRoutes.DISPENSER_AGREEMENT_HISTORY
  //      }, null, { queryParams: { page: page } });

  public pageChange(page: number): void {
    // TODO:Spartacus - 'RoutingService.go' changed signature. Before 4.0, the object with query params could be passed in the 2nd argument. Now the 2nd argument is Angular NavigationExtras object (with 'queryParams' property).
    this.routingService.go(
      {
        cxRoute: CustomRoutes.DISPENSER_AGREEMENT_HISTORY
        // }, null, {queryParams: {page: page}});
      },
      { queryParams: { page: page } }
    )

    this.selectedPage = page
    this.changePageData({
      pageNumber: page,
      sort: this.selectedSort,
      status: this.selectedFilter,
      searchText: this.selectedSearch
    })
  }
  public filterChange(value: string): void {
    this.selectedFilter = value
    this.changePageData({
      status: value,
      sort: this.selectedSort,
      searchText: this.selectedSearch
    })
  }

  public sortChange(value: string): void {
    this.selectedSort = value
    this.changePageData({
      status: this.selectedFilter,
      sort: value,
      searchText: this.selectedSearch
    })
  }

  public changePageData(params: ISharedPagination): void {
    this.showLoaderOnPagination = true
    this.dispenserAgreementService
      .getAgreements(params)
      .pipe(
        finalize(() => {
          this.showInitialLoader = false
          this.showLoaderOnPagination = false
          this.changeDetectorRef.detectChanges()
        })
      )
      .subscribe((res) => {
        if (!res) {
          this.globalMessageService.add(SharedValue.errorTryAgain, GlobalMessageType.MSG_TYPE_ERROR)
        } else {
          this.agreementList = res
        }
      })
  }

  public onSearch(value): void {
    this.selectedSearch = value?.trim()
    this.changePageData({
      sort: this.selectedSort,
      status: this.selectedFilter,
      searchText: this.selectedSearch
    })
  }

  public get showMessageWhenNoData(): boolean {
    return !this.showLoaderOnPagination
  }

  public get showLoaderOnStartupOrWhenNoData(): boolean {
    return !this.agreementList?.results?.length && this.showLoaderOnPagination
  }

  public downloadOpenShipmentsOrders(): void {
    this.downloadingOpenShipmentsOrdersCsv = true
    this.dispenserAgreementService.downloadOpenShipmentOrders().subscribe(
      (res) => {
        const todayDate = formatDate(new Date(), 'MMMM d, y', 'en')
        const fileName = 'Dispenser Shipments Orders ' + todayDate
        const csv: ICsv = {
          title: fileName,
          header: 'Dispenser Shipments Orders: ' + todayDate,
          columns: [
            { title: 'dispenserShipment.shipmentNumber', key: 'code' },
            { title: 'dispenserShipment.orderDate', key: 'shipmentOrderDate', type: CustomTableColumnType.DATE },
            { title: 'dispenserShipment.agreementNumber', key: 'quote.orderCode' },
            { title: 'dispenserShipment.poNumber', key: 'poNumber' },
            { title: 'shared.status', key: 'status' },
            { title: 'dispenserShipment.dispenserShipTo', key: 'address.formattedAddress' },
            { title: 'dispenserShipment.productNumber', key: 'entry.product.code' },
            { title: 'dispenserShipment.productShortDescription', key: 'entry.product.name' },
            { title: 'dispenserShipment.etaDate', key: 'entry.namedDeliveryDate', type: CustomTableColumnType.DATE },
            { title: 'dispenserShipment.lineStatus', key: 'entry.shipmentEntryStatus' },
            { title: 'dispenserShipment.orderedQuantity', key: 'entry.quantitySelected' },
            { title: 'dispenserShipment.deliveredQuantity', key: 'entry.quantityDispatched' }
          ],
          rows: this.csvService.extractEntries(res.results, 'entries')
        }

        this.downloadingOpenShipmentsOrdersCsv = false
        this.csvService.generateCSV(csv)
        this.changeDetectorRef.detectChanges()
      },
      (err) => {
        this.downloadingOpenShipmentsOrdersCsv = false
        this.globalMessageService.add(err.message, GlobalMessageType.MSG_TYPE_ERROR)
        this.changeDetectorRef.detectChanges()
      }
    )
  }
}
